import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Printer, ArrowLeft, ArrowRight, Lock, Star } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";

const CertificateCard = ({ name, stars, date }) => {
  const { t, dir, lang } = useLang();
  const c = t.cert;
  const font = lang === "ar" ? "font-display" : "font-latin";
  return (
    <div id="certificate" dir={dir} data-testid="certificate-card" className="relative w-full aspect-[297/210] bg-[#FFFDF7] text-[#1C1917] [container-type:inline-size] shadow-[0_30px_60px_-30px_rgba(28,25,23,0.5)]">
      <div className="absolute inset-[2.2cqw] border-[0.5cqw] border-[#B7791F]" />
      <div className="absolute inset-[3.3cqw] border-[0.15cqw] border-[#B7791F]/70" />
      {["top-[1.4cqw] start-[1.4cqw]", "top-[1.4cqw] end-[1.4cqw]", "bottom-[1.4cqw] start-[1.4cqw]", "bottom-[1.4cqw] end-[1.4cqw]"].map((p) => (
        <Star key={p} className={`absolute ${p} fill-[#D4A017] text-[#B7791F]`} style={{ width: "3cqw", height: "3cqw" }} />
      ))}
      <div className="absolute inset-[5cqw] flex flex-col items-center justify-between text-center">
        <div className="flex items-center">
          <img src="/images/yallahero-logo-v2.webp" alt="Yalla Hero" style={{ height: "9cqw" }} className="w-auto" />
        </div>
        <div className="flex flex-col items-center gap-[1.2cqw]">
          <h1 className={`${font} font-bold text-[#8C5A12]`} style={{ fontSize: "5.4cqw", lineHeight: 1.1 }} data-testid="certificate-title">{c.title}</h1>
          <p style={{ fontSize: "1.9cqw" }} className="text-stone-700">{c.given}</p>
          <p className="font-display font-bold border-b-[0.2cqw] border-[#B7791F] px-[3cqw] pb-[0.6cqw]" style={{ fontSize: "6cqw", lineHeight: 1.2 }} dir="auto" data-testid="certificate-name">{name}</p>
          <p style={{ fontSize: "1.9cqw" }} className="max-w-[75cqw] text-stone-800" data-testid="certificate-reason">{c.reason(stars)}</p>
        </div>
        <div className="w-full flex items-end justify-between">
          <div className="text-center" style={{ fontSize: "1.6cqw" }}>
            <p className="font-bold" data-testid="certificate-date">{date}</p>
            <p className="text-stone-600 border-t-[0.15cqw] border-stone-400 mt-[0.4cqw] pt-[0.4cqw] px-[2cqw]">{c.date}</p>
          </div>
          <div className="relative -mb-[1cqw]" style={{ width: "11cqw", height: "11cqw" }}>
            <span className="absolute inset-0 rounded-full grid place-items-center" style={{ background: "linear-gradient(145deg,#FDE68A,#B7791F)", boxShadow: "0 0 0 0.4cqw #D4A017" }}>
              <Star className="fill-white text-white" style={{ width: "5.5cqw", height: "5.5cqw" }} />
            </span>
          </div>
          <div className="text-center" style={{ fontSize: "1.6cqw" }}>
            <p className="font-bold" data-testid="certificate-stars">{stars}</p>
            <p className="text-stone-600 border-t-[0.15cqw] border-stone-400 mt-[0.4cqw] pt-[0.4cqw] px-[2cqw]">{c.stars}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function Certificate() {
  const { user } = useAuth();
  const { t, lang, isRtl } = useLang();
  const [data, setData] = useState(null);
  const Back = isRtl ? ArrowRight : ArrowLeft;

  useEffect(() => {
    if (!user) return;
    api.get("/my/stars").then((r) => setData(r.data)).catch(() => setData({ level: 0, stars: 0 }));
  }, [user]);

  const ok = (data?.level ?? 0) >= 3;
  const date = new Date().toLocaleDateString(lang, { day: "numeric", month: "long", year: "numeric" });

  return (
    <div className="min-h-screen bg-[#F2ECE1] px-3 sm:px-8 py-6 sm:py-10" data-testid="certificate-page">
      <div className="max-w-5xl mx-auto">
        <div className="no-print flex flex-wrap items-center justify-between gap-3 mb-6">
          <Link to="/library" data-testid="certificate-back-link" className="inline-flex items-center gap-2 text-sm font-semibold hover:text-[#8B3A2B]"><Back className="w-4 h-4" /> {t.cert.back}</Link>
          {ok && (
            <button onClick={() => window.print()} data-testid="certificate-print-btn"
              className="inline-flex items-center gap-2 rounded-full bg-[#1C1917] text-[#FAF7F2] px-6 py-3 font-semibold hover:bg-[#8C5A12] transition-colors">
              <Printer className="w-5 h-5" /> {t.cert.print}
            </button>
          )}
        </div>
        {data && !ok && (
          <p className="no-print bg-white border border-[#E5DCCB] p-8 text-center text-stone-700 flex flex-col items-center gap-3" data-testid="certificate-locked">
            <Lock className="w-8 h-8 text-[#8B3A2B]" /> {t.cert.locked}
          </p>
        )}
        {ok && <CertificateCard name={user?.name ?? ""} stars={data.stars ?? 0} date={date} />}
      </div>
    </div>
  );
}
